import type { ReactNode } from "react";
import NextLink from "next/link";
import { Tag, TagLabel, TagRightIcon, forwardRef } from "@chakra-ui/react";
import type { SystemStyleObject, TagProps } from "@chakra-ui/react";
import { RiExternalLinkLine } from "react-icons/ri";

export interface TagLinkProps extends TagProps {
  /**
   * A react node to be placed inside a `TagLabel` element inside the component
   */
  label?: ReactNode;
  /**
   * If passed, the component will have an external link icon
   */
  hasIcon?: boolean;
  /**
   * The target URL of the anchor element
   */
  href?: string;
  /**
   * Whether or not the link points to an outside website
   *
   * @defaultValue `false`
   */
  isExternal?: boolean;
  /**
   * Whether or not to prefetch the page for the NextLink, only applies when
   * `isExtenal` is set to `false`
   *
   * @defaultValue `true`
   */
  prefetch?: boolean;
}

const TagLink = forwardRef<TagLinkProps, "a">(
  (
    {
      children,
      href,
      isExternal = false,
      label,
      hasIcon = false,
      colorScheme = "gray",
      variant = "subtle",
      size = ["sm", "md"],
      ...props
    },
    ref
  ) => {
    const activeStyles: Record<string, SystemStyleObject> = {
      outline: {
        color: `${colorScheme}.700`,
        borderWidth: 1,
      },
      solid: {
        color: "white",
      },
    };
    const activeStyle =
      typeof variant === "string" && variant in activeStyles
        ? activeStyles[variant]
        : {
            color: `${colorScheme}.900`,
          };

    const getAs = () => {
      if (!href) {
        return "span";
      }

      if (isExternal) {
        return "a";
      }

      return NextLink;
    };

    return (
      <Tag
        as={getAs()}
        href={href}
        ref={ref}
        aria-label={typeof label === "string" ? label : undefined}
        rel={href && isExternal ? "noopener noreferrer" : undefined}
        target={href && isExternal ? "_blank" : undefined}
        colorScheme={colorScheme}
        variant={variant}
        size={size}
        _active={activeStyle}
        _hover={{
          textDecoration: "underline",
        }}
        {...props}
      >
        {label && (
          <TagLabel
            display="inline-block"
            whiteSpace="nowrap"
            overflow="hidden"
            textOverflow="ellipsis"
            width="min-content"
          >
            {label}
          </TagLabel>
        )}
        {children}
        {!!href && isExternal && hasIcon && (
          <TagRightIcon as={RiExternalLinkLine} />
        )}
      </Tag>
    );
  }
);

export default TagLink;
